import { ref } from 'vue'
import { defineStore } from 'pinia'
import { Assignment, AssignmentAnswer, AssignmentResponse, ParsedAssignmentAnswer } from '../models/Assignment'
import useAssignmentApi from '../api/assignmentApi'
import { StudentProgress } from '../models/Activity'

export const useAssignmentStore = defineStore('assignments',  () => {
  const {
    getAssignmentResponse,
    getAssignment,
  } = useAssignmentApi()
  const assignment = ref<Assignment|undefined>(undefined)
  const assignmentResponse = ref<AssignmentResponse[]>([])

  async function loadAssignments(assignmentKey: string): Promise<void> {
    try {
      assignmentResponse.value = await getAssignmentResponse(assignmentKey)
      assignment.value = await getAssignment(assignmentKey)
    } catch (error) {
      throw new Error(`Could not get assignments with id: ${assignmentKey}`)
    }
  }

  /**
   * Decorates the student progress with the assignment answer if there is one
   */
  function decorateStudentProgress(studentProgress: StudentProgress[]): StudentProgress[] {
    return studentProgress.map((progress: StudentProgress) => {
      const decorator: AssignmentResponse | undefined = assignmentResponse.value.find((assignmentResponse: AssignmentResponse) => assignmentResponse.student.userId === progress.user.userId)
      progress.assignmentAnswer = null

      if (typeof decorator === 'undefined') {
        return progress
      }

      if (decorator?.answer?.length > 0) {
        const latest = getLatestAssignmentAnswer(decorator)
        if(latest === null) {
          return progress
        }

        const text  = createAssignmentText(latest)

        progress.assignmentAnswer = text
        progress.totalFields = Object.keys(text).length
        progress.filledFields = Object.values(text).filter((value: string|null) => value !== '').length
      }
      return progress
    })
  }

  function getLatestAssignmentAnswer(assignmentResponse: AssignmentResponse): AssignmentAnswer | null {
    return assignmentResponse.answer.reduce((a: AssignmentAnswer, b: AssignmentAnswer) => a.version > b.version ? a : b)
  }

  async function loadDecoratedStudentProgress(studentProgress: StudentProgress[], assignmentKey: string): Promise<StudentProgress[]> {
    await loadAssignments(assignmentKey)
    return decorateStudentProgress(studentProgress)
  }

  function createAssignmentText(assignmentAnswer: AssignmentAnswer): ParsedAssignmentAnswer {
    const template: string | undefined = assignment.value?.template
    if (template === undefined) {
      return {}
    }

    const parsedTemplate: string[] = Object.values(JSON.parse(template))
    const parsed = JSON.parse(assignmentAnswer.data).fields

    const answerSheet: {[index: string]:string} = {}
    parsedTemplate.forEach((field: string, index: number): void => {
        answerSheet[field] = parsed[index] || ''
    })

    return answerSheet
  }

  function getFormWriterIdFromStudentProgress(studentProgress: StudentProgress[]): string {
    return studentProgress.find(
      (studentProgress: StudentProgress) => typeof studentProgress.formWriterId === 'string' && studentProgress.formWriterId.length > 0
    )?.formWriterId ?? ''
  }

  return {
    loadDecoratedStudentProgress,
    getFormWriterIdFromStudentProgress,
  }
})
